import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Plus, Trash2, Pencil, Sparkles, Users as UsersIcon, ClipboardCheck, ChevronDown, ChevronUp, Loader2, ArrowRight,
} from "lucide-react";
import { toast } from "sonner";

const TYPE_LABEL = {
  multiple_choice: "Opción múltiple",
  scale: "Escala 1-5",
  open: "Pregunta abierta",
};

function emptyQuestion() {
  return { text: "", type: "multiple_choice", options: ["", "", "", ""], correct_index: 0, weight: 1 };
}

function QuestionEditor({ q, onChange, onRemove, idx }) {
  const update = (patch) => onChange({ ...q, ...patch });
  return (
    <div className="bg-[#FFFFFF] border border-[#E5E7EB] rounded-xl p-4" data-testid={`q-editor-${idx}`}>
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs text-[#6B7280] uppercase tracking-[0.18em]">Pregunta {idx + 1}</p>
        <div className="flex items-center gap-2">
          <select
            value={q.type}
            onChange={(e) => update({
              type: e.target.value,
              options: e.target.value === "multiple_choice" ? (q.options?.length ? q.options : ["", "", "", ""]) : [],
              correct_index: e.target.value === "multiple_choice" ? (q.correct_index ?? 0) : null,
            })}
            className="border border-[#E5E7EB] rounded-md px-2 py-1.5 text-xs bg-white"
            data-testid={`q-type-${idx}`}
          >
            <option value="multiple_choice">Opción múltiple</option>
            <option value="scale">Escala 1-5</option>
            <option value="open">Abierta</option>
          </select>
          <button
            onClick={onRemove}
            className="text-[#DC2626] hover:bg-[#DC2626]/10 p-1 rounded-md"
            data-testid={`q-remove-${idx}`}
            aria-label="Eliminar pregunta"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>
      <Textarea
        placeholder="Texto de la pregunta..."
        value={q.text}
        onChange={(e) => update({ text: e.target.value })}
        rows={2}
        data-testid={`q-text-${idx}`}
      />
      {q.type === "multiple_choice" && (
        <div className="space-y-2 mt-3">
          {(q.options || []).map((opt, i) => (
            <div key={i} className="flex items-center gap-2">
              <input
                type="radio"
                checked={q.correct_index === i}
                onChange={() => update({ correct_index: i })}
                data-testid={`q-correct-${idx}-${i}`}
                aria-label={`Marcar opción ${i + 1} como correcta`}
              />
              <Input
                placeholder={`Opción ${i + 1}`}
                value={opt}
                onChange={(e) => {
                  const next = [...q.options];
                  next[i] = e.target.value;
                  update({ options: next });
                }}
                className="flex-1"
                data-testid={`q-opt-${idx}-${i}`}
              />
            </div>
          ))}
          <p className="text-xs text-[#6B7280]">El radio marca la respuesta correcta. Deja sin marcar si no hay correcta.</p>
        </div>
      )}
      {q.type === "scale" && (
        <p className="text-xs text-[#6B7280] mt-2">El usuario verá una escala 1-5 (sin respuesta correcta).</p>
      )}
      {q.type === "open" && (
        <p className="text-xs text-[#6B7280] mt-2">El usuario escribirá una respuesta libre (no se califica automáticamente).</p>
      )}
    </div>
  );
}

function CreateOrEditDialog({ target, evaluation, onSaved, triggerLabel, triggerVariant = "default" }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    title: "",
    description: "",
    duration_minutes: 20,
    passing_score: 70,
    questions: [emptyQuestion()],
  });
  const [busy, setBusy] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiTopic, setAiTopic] = useState("");
  const [aiCount, setAiCount] = useState(10);

  useEffect(() => {
    if (open && evaluation) {
      setForm({
        title: evaluation.title || "",
        description: evaluation.description || "",
        duration_minutes: evaluation.duration_minutes || 20,
        passing_score: evaluation.passing_score || 70,
        questions: (evaluation.questions || []).map((q) => ({
          ...q,
          options: q.options || [],
          correct_index: q.correct_index ?? null,
        })),
      });
    } else if (open && !evaluation) {
      setForm({
        title: "",
        description: "",
        duration_minutes: 20,
        passing_score: 70,
        questions: [emptyQuestion()],
      });
    }
  }, [open, evaluation]);

  const addQ = () => setForm((f) => ({ ...f, questions: [...f.questions, emptyQuestion()] }));
  const updateQ = (idx, q) => setForm((f) => ({ ...f, questions: f.questions.map((x, i) => i === idx ? q : x) }));
  const removeQ = (idx) => setForm((f) => ({ ...f, questions: f.questions.filter((_, i) => i !== idx) }));

  const generateAI = async () => {
    if (!form.title) {
      toast.error("Pon un título primero para que la IA tenga contexto.");
      return;
    }
    setAiBusy(true);
    try {
      const r = await api.post("/admin/evaluations/ai/generate", {
        title: form.title, target, topic: aiTopic, n_questions: aiCount,
      });
      setForm((f) => ({ ...f, questions: r.data.questions }));
      toast.success(`${r.data.questions.length} preguntas generadas con IA`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error generando con IA");
    } finally {
      setAiBusy(false);
    }
  };

  const submit = async () => {
    if (!form.title.trim()) return toast.error("El título es obligatorio");
    if (!form.questions.length) return toast.error("Agrega al menos una pregunta");
    if (form.questions.some((q) => !q.text.trim())) return toast.error("Hay preguntas sin texto");
    setBusy(true);
    try {
      const payload = { ...form, target };
      if (evaluation) {
        await api.patch(`/admin/evaluations/${evaluation.id}`, payload);
      } else {
        await api.post("/admin/evaluations", payload);
      }
      toast.success(evaluation ? "Evaluación actualizada" : "Evaluación creada");
      setOpen(false);
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {triggerVariant === "icon" ? (
          <button
            data-testid={`edit-eval-${evaluation?.id}`}
            className="inline-flex items-center gap-1 text-[#1B2E7A] hover:underline text-xs"
          >
            <Pencil className="h-3.5 w-3.5" /> Editar
          </button>
        ) : (
          <Button className="bg-[#1B2E7A] hover:bg-[#13205A] text-white" data-testid="create-eval-btn">
            <Plus className="h-4 w-4 mr-1" /> {triggerLabel || "Crear evaluación"}
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="bg-white max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="eval-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">
            {evaluation ? "Editar evaluación" : `Nueva evaluación · ${target === "manager" ? "Managers" : "Vendedores"}`}
          </DialogTitle>
          <DialogDescription className="text-xs text-[#6B7280]">
            Define título, duración, puntaje aprobatorio y preguntas. Puedes generar las preguntas con IA.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <Input
            placeholder="Título"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            data-testid="eval-title"
          />
          <Textarea
            placeholder="Descripción (opcional)"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            rows={2}
            data-testid="eval-description"
          />
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-[#6B7280]">Duración (min)</label>
              <Input
                type="number"
                value={form.duration_minutes}
                onChange={(e) => setForm({ ...form, duration_minutes: Number(e.target.value) })}
                data-testid="eval-duration"
              />
            </div>
            <div>
              <label className="text-xs text-[#6B7280]">Puntaje aprobatorio</label>
              <Input
                type="number"
                min={0}
                max={100}
                value={form.passing_score}
                onChange={(e) => setForm({ ...form, passing_score: Number(e.target.value) })}
                data-testid="eval-passing"
              />
            </div>
          </div>

          {/* AI Generation Block */}
          <div className="bg-[#FEF3C7] border border-[#F4B81F]/40 rounded-xl p-4">
            <p className="text-xs font-medium text-[#854D0E] mb-2 inline-flex items-center gap-1.5">
              <Sparkles className="h-3.5 w-3.5" /> Generar preguntas con IA (Claude Sonnet 4.5)
            </p>
            <div className="flex flex-col sm:flex-row gap-2">
              <Input
                placeholder="Tema o foco (ej. servicio al cliente)"
                value={aiTopic}
                onChange={(e) => setAiTopic(e.target.value)}
                className="flex-1"
                data-testid="ai-topic"
              />
              <Input
                type="number"
                min={5}
                max={20}
                value={aiCount}
                onChange={(e) => setAiCount(Math.max(5, Math.min(20, Number(e.target.value))))}
                className="w-24"
                data-testid="ai-count"
              />
              <Button
                onClick={generateAI}
                disabled={aiBusy}
                className="bg-[#F4B81F] hover:bg-[#D49538] text-[#1A1A1A]"
                data-testid="ai-gen-btn"
              >
                {aiBusy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1" />}
                {aiBusy ? "Generando..." : "Generar"}
              </Button>
            </div>
            <p className="text-[10px] text-[#6B7280] mt-2">Reemplaza las preguntas actuales con las generadas.</p>
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium text-[#1A1A1A]">Preguntas ({form.questions.length})</p>
              <Button onClick={addQ} variant="outline" size="sm" data-testid="add-q-btn">
                <Plus className="h-3.5 w-3.5 mr-1" /> Pregunta
              </Button>
            </div>
            {form.questions.map((q, i) => (
              <QuestionEditor key={i} idx={i} q={q} onChange={(nq) => updateQ(i, nq)} onRemove={() => removeQ(i)} />
            ))}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
          <Button onClick={submit} disabled={busy} className="bg-[#1B2E7A] hover:bg-[#13205A] text-white" data-testid="save-eval-btn">
            {busy ? "Guardando..." : "Guardar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AssignDialog({ evaluation, target, onSaved }) {
  const [open, setOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/admin/users").then((r) => {
      // Filter by target
      const filtered = r.data.filter((u) => {
        if (u.role === "admin") return false;
        const ut = u.user_type || "salesperson";
        return ut === target;
      });
      setUsers(filtered);
      setSelected(new Set());
    });
  }, [open, target]);

  const toggle = (uid) => {
    const next = new Set(selected);
    if (next.has(uid)) next.delete(uid); else next.add(uid);
    setSelected(next);
  };

  const submit = async () => {
    if (!selected.size) return toast.error("Selecciona al menos un usuario");
    setBusy(true);
    try {
      const r = await api.post(`/admin/evaluations/${evaluation.id}/assign`, { user_ids: [...selected] });
      toast.success(`${r.data.added} ${target === "manager" ? "manager(s)" : "vendedor(es)"} asignado(s)`);
      setOpen(false);
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button
          className="inline-flex items-center gap-1 text-xs text-[#1B2E7A] hover:underline"
          data-testid={`assign-${evaluation.id}`}
        >
          <UsersIcon className="h-3.5 w-3.5" /> Asignar
        </button>
      </DialogTrigger>
      <DialogContent className="bg-white max-h-[80vh] overflow-y-auto" data-testid="assign-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Asignar a {target === "manager" ? "managers" : "vendedores"}</DialogTitle>
          <DialogDescription className="text-xs text-[#6B7280]">
            Marca a las personas que recibirán esta evaluación. Solo verás usuarios del tipo seleccionado.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2 max-h-96 overflow-y-auto">
          {users.length === 0 && (
            <p className="text-sm text-[#6B7280] text-center py-6">
              No hay {target === "manager" ? "managers" : "vendedores"} aún. Asigna el rol desde Vendedores y avance.
            </p>
          )}
          {users.map((u) => (
            <label
              key={u.user_id}
              className="flex items-center gap-3 px-3 py-2 rounded-md hover:bg-[#F3F4F6] cursor-pointer"
              data-testid={`assign-user-${u.user_id}`}
            >
              <input type="checkbox" checked={selected.has(u.user_id)} onChange={() => toggle(u.user_id)} />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-[#1A1A1A] truncate">{u.name}</p>
                <p className="text-xs text-[#6B7280] truncate">{u.email}</p>
              </div>
            </label>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
          <Button onClick={submit} disabled={busy} className="bg-[#1B2E7A] hover:bg-[#13205A] text-white" data-testid="assign-save-btn">
            {busy ? "Asignando..." : `Asignar ${selected.size > 0 ? `(${selected.size})` : ""}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ResultsDialog({ evaluation }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get(`/admin/evaluations/${evaluation.id}/results`)
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, [open, evaluation.id]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button className="inline-flex items-center gap-1 text-xs text-[#6B7280] hover:text-[#1B2E7A]" data-testid={`results-${evaluation.id}`}>
          <ClipboardCheck className="h-3.5 w-3.5" /> Resultados
        </button>
      </DialogTrigger>
      <DialogContent className="bg-white max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="results-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Resultados · {evaluation.title}</DialogTitle>
          <DialogDescription className="text-xs text-[#6B7280]">
            Lista de personas asignadas con su puntaje, estado y resumen IA al expandir cada fila.
          </DialogDescription>
        </DialogHeader>
        {loading && <div className="text-[#374151]">Cargando...</div>}
        {!loading && data && (
          <div className="space-y-3">
            {data.rows.length === 0 && <p className="text-sm text-[#6B7280]">Aún sin asignaciones.</p>}
            {data.rows.map((r) => (
              <div key={r.user.user_id} className="bg-[#FFFFFF] border border-[#E5E7EB] rounded-md p-4">
                <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
                  <div>
                    <p className="text-sm font-medium text-[#1A1A1A]">{r.user.name}</p>
                    <p className="text-xs text-[#6B7280]">{r.user.email}</p>
                  </div>
                  {r.attempt ? (
                    <div className="text-right">
                      <p className={`text-lg font-heading ${r.attempt.passed ? "text-[#1B2E7A]" : "text-[#DC2626]"}`}>
                        {r.attempt.score}/100
                      </p>
                      <p className="text-[10px] text-[#6B7280]">{r.attempt.passed ? "Aprobó" : "Pendiente"}</p>
                    </div>
                  ) : (
                    <span className="text-xs text-[#B45309]">Sin intentar</span>
                  )}
                </div>
                {r.attempt && r.attempt.summary && (
                  <details>
                    <summary className="text-xs text-[#1B2E7A] cursor-pointer">Ver resumen IA y desglose</summary>
                    <div className="mt-2 space-y-2 text-sm">
                      <p className="text-[#374151]">{r.attempt.summary.summary_es}</p>
                      {r.attempt.summary.strengths_es?.length > 0 && (
                        <div>
                          <p className="text-xs uppercase tracking-[0.18em] text-[#1B2E7A] mb-1">Fortalezas</p>
                          <ul className="text-xs text-[#1A1A1A] list-disc pl-4">
                            {r.attempt.summary.strengths_es.map((s, i) => <li key={i}>{s}</li>)}
                          </ul>
                        </div>
                      )}
                      {r.attempt.summary.to_improve_es?.length > 0 && (
                        <div>
                          <p className="text-xs uppercase tracking-[0.18em] text-[#DC2626] mb-1">A mejorar</p>
                          <ul className="text-xs text-[#1A1A1A] list-disc pl-4">
                            {r.attempt.summary.to_improve_es.map((s, i) => <li key={i}>{s}</li>)}
                          </ul>
                        </div>
                      )}
                    </div>
                  </details>
                )}
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function AdminEvaluations({ target = "salesperson" }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/evaluations", { params: { target } });
      setItems(r.data.evaluations || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [target]);

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar esta evaluación? También se borran asignaciones e intentos.")) return;
    try {
      await api.delete(`/admin/evaluations/${id}`);
      toast.success("Eliminada");
      load();
    } catch {
      toast.error("Error");
    }
  };

  return (
    <div data-testid={`admin-evaluations-${target}-page`} className="space-y-8">
      <div>
        <p className="text-xs uppercase tracking-[0.2em] text-[#6B7280]">
          {target === "manager" ? "Eval. Managers" : "Evaluaciones"}
        </p>
        <h1 className="font-heading text-4xl sm:text-5xl text-[#1A1A1A] mb-3">
          {target === "manager" ? "Exámenes para managers" : "Exámenes para vendedores"}.
        </h1>
        <p className="text-[#6B7280] max-w-2xl">
          Crea exámenes (manualmente o con IA), asígnalos a {target === "manager" ? "tus managers" : "tu equipo"} y revisa los resultados con resumen automático de fortalezas y áreas de mejora.
        </p>
      </div>

      <div className="flex justify-end">
        <CreateOrEditDialog target={target} onSaved={load} />
      </div>

      {loading ? (
        <div className="text-[#374151]">Cargando...</div>
      ) : items.length === 0 ? (
        <div className="bg-white border border-[#E5E7EB] rounded-2xl p-10 text-center">
          <p className="text-[#6B7280]">Aún no hay evaluaciones. Crea la primera arriba.</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {items.map((ev) => (
            <div key={ev.id} className="bg-white border border-[#E5E7EB] rounded-2xl p-5" data-testid={`eval-card-${ev.id}`}>
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1 min-w-0">
                  <h3 className="font-heading text-xl text-[#1A1A1A] truncate">{ev.title}</h3>
                  {ev.description && <p className="text-xs text-[#6B7280] mt-1 line-clamp-2">{ev.description}</p>}
                </div>
                <span className="text-[10px] uppercase tracking-[0.18em] text-[#6B7280] bg-[#F3F4F6] px-2 py-1 rounded">
                  {ev.questions?.length || 0} pts
                </span>
              </div>
              <div className="grid grid-cols-3 gap-2 text-xs text-[#6B7280] mb-4 mt-3">
                <div>
                  <p className="text-[#6B7280] uppercase tracking-[0.16em] text-[10px]">Duración</p>
                  <p className="font-medium text-[#1A1A1A]">{ev.duration_minutes} min</p>
                </div>
                <div>
                  <p className="text-[#6B7280] uppercase tracking-[0.16em] text-[10px]">Asignados</p>
                  <p className="font-medium text-[#1A1A1A]">{ev.assigned_count}</p>
                </div>
                <div>
                  <p className="text-[#6B7280] uppercase tracking-[0.16em] text-[10px]">Completados</p>
                  <p className="font-medium text-[#1A1A1A]">{ev.completed_count}</p>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-3 pt-3 border-t border-[#E5E7EB]">
                <CreateOrEditDialog target={target} evaluation={ev} onSaved={load} triggerVariant="icon" />
                <AssignDialog evaluation={ev} target={target} onSaved={load} />
                <ResultsDialog evaluation={ev} />
                <button
                  onClick={() => remove(ev.id)}
                  className="ml-auto inline-flex items-center gap-1 text-xs text-[#DC2626] hover:underline"
                  data-testid={`del-eval-${ev.id}`}
                >
                  <Trash2 className="h-3.5 w-3.5" /> Eliminar
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
